import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, getDoc, doc, orderBy, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Bid, ProxyBid } from '../types/bid.types';

type ViewMode = 'tile' | 'list';
const VIEW_KEY = 'mybids_view';

const isVoided = (b: Bid) => b.status === 'rejected' || !!b.voidReason;
const isAuto = (b: Bid) => b.bidType === 'proxy-auto' || b.systemGenerated === true;

interface LotCluster {
  key: string;
  auctionId: string;
  lotId: string;
  lotNumber: string;
  lotTitle: string;
  bids: Bid[];        // ALL of the user's bids on the lot (incl. voided), newest first
  activeCount: number;
  status: 'winning' | 'outbid';
  displayAmount: number; // their highest ACTIVE bid (or highest overall if all voided)
  hasVoided: boolean;
}

const MyBids: React.FC = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [bids, setBids] = useState<Bid[]>([]);
  const [proxies, setProxies] = useState<ProxyBid[]>([]);
  const [auctionNames, setAuctionNames] = useState<Record<string, string>>({});
  const [lotMeta, setLotMeta] = useState<Record<string, { lotNumber?: string; title?: string }>>({});
  const [selectedAuctionId, setSelectedAuctionId] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<ViewMode>(() => (localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'tile'));
  const setView = (m: ViewMode) => { setViewMode(m); try { localStorage.setItem(VIEW_KEY, m); } catch { /* ignore */ } };

  const [dismissedBids, setDismissedBids] = useState<Record<string, number>>({});
  const [showDismissed, setShowDismissed] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleExpand = (key: string) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  useEffect(() => {
    loadMyBids();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  const loadMyBids = async () => {
    if (!currentUser) { setLoading(false); return; }
    try {
      const bidsQuery = query(collection(db, 'bids'), where('bidderId', '==', currentUser.uid), orderBy('placedAt', 'desc'));
      const proxyQuery = query(collection(db, 'proxyBids'), where('bidderId', '==', currentUser.uid), where('isActive', '==', true));
      const [bidsSnap, proxySnap, userSnap] = await Promise.all([
        getDocs(bidsQuery), getDocs(proxyQuery), getDoc(doc(db, 'users', currentUser.uid)),
      ]);

      const bidsList: Bid[] = bidsSnap.docs.map((d) => {
        const data = d.data();
        return { id: d.id, ...data, placedAt: data.placedAt?.toDate() } as Bid;
      });
      const proxyList: ProxyBid[] = proxySnap.docs.map((d) => {
        const data = d.data();
        return { id: d.id, ...data, createdAt: data.createdAt?.toDate?.() } as ProxyBid;
      });

      const auctionIds = Array.from(new Set<string>([...bidsList.map((b) => b.auctionId), ...proxyList.map((p) => p.auctionId)]));
      const names: Record<string, string> = {};
      const lotMetaMap: Record<string, { lotNumber?: string; title?: string }> = {};
      await Promise.all(auctionIds.map(async (id) => {
        try {
          const aSnap = await getDoc(doc(db, 'auctions', id));
          if (aSnap.exists()) {
            const data = aSnap.data() as any;
            names[id] = data.title || id;
            (data.lots || []).forEach((l: any) => { lotMetaMap[`${id}_${l.id}`] = { lotNumber: l.lotNumber, title: l.title }; });
          } else { names[id] = id; }
        } catch { names[id] = id; }
      }));

      setBids(bidsList);
      setProxies(proxyList);
      setAuctionNames(names);
      setLotMeta(lotMetaMap);
      setDismissedBids((userSnap.exists() && (userSnap.data() as any).dismissedBids) || {});
    } catch (error: any) {
      console.error('❌ Error loading bids:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const dismiss = async (auctionId: string, lotId: string) => {
    if (!currentUser) return;
    const key = `${auctionId}_${lotId}`;
    const ts = Date.now();
    setDismissedBids((d) => ({ ...d, [key]: ts }));
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { [`dismissedBids.${key}`]: ts });
    } catch (e) {
      setDismissedBids((d) => { const n = { ...d }; delete n[key]; return n; });
      console.error('Failed to dismiss:', e);
    }
  };
  const restore = async (auctionId: string, lotId: string) => {
    if (!currentUser) return;
    const key = `${auctionId}_${lotId}`;
    const prev = dismissedBids[key];
    setDismissedBids((d) => { const n = { ...d }; delete n[key]; return n; });
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { [`dismissedBids.${key}`]: deleteField() });
    } catch (e) {
      if (prev != null) setDismissedBids((d) => ({ ...d, [key]: prev }));
      console.error('Failed to restore:', e);
    }
  };

  if (!currentUser) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 mb-4">Please sign in to view your bids</p>
        <Link to="/login" className="btn-primary">Sign In</Link>
      </div>
    );
  }
  if (loading) return <div className="text-center py-12"><p className="text-gray-600">Loading your bids...</p></div>;
  if (error) {
    return (
      <div className="text-center py-12">
        <div className="card max-w-2xl mx-auto">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Bids</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button onClick={loadMyBids} className="mt-4 btn-primary">Try Again</button>
        </div>
      </div>
    );
  }
  if (bids.length === 0 && proxies.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">My Bids</h1>
        <div className="card text-center py-12">
          <p className="text-gray-600 mb-4">You haven't placed any bids yet</p>
          <Link to="/browse" className="btn-primary">Browse Auctions</Link>
        </div>
      </div>
    );
  }

  const proxyByLot = new Map<string, ProxyBid>();
  proxies.forEach((p) => proxyByLot.set(`${p.auctionId}_${p.lotId}`, p));

  const auctionOptions = Array.from(new Set<string>([...bids.map((b) => b.auctionId), ...proxies.map((p) => p.auctionId)]));

  // ---- CLUSTER by lot. bids arrive newest-first (placedAt desc), preserved per cluster. ----
  const clusterMap = new Map<string, LotCluster>();
  for (const b of bids) {
    // 'pending' = in flight, not yet granted by the server — not one of "your bids" yet.
    if (b.status === 'pending') continue;
    const key = `${b.auctionId}_${b.lotId}`;
    let c = clusterMap.get(key);
    if (!c) {
      c = { key, auctionId: b.auctionId, lotId: b.lotId, lotNumber: b.lotNumber, lotTitle: b.lotTitle,
        bids: [], activeCount: 0, status: 'outbid', displayAmount: 0, hasVoided: false };
      clusterMap.set(key, c);
    }
    c.bids.push(b);
  }
  const allClusters: LotCluster[] = [];
  clusterMap.forEach((c) => {
    const active = c.bids.filter((b) => !isVoided(b));
    c.activeCount = active.length;
    c.hasVoided = c.bids.some(isVoided);
    // SECTION RULE: a lot is WINNING if ANY of the user's non-voided bids on it is winning;
    // otherwise OUTBID. So each lot appears in EXACTLY ONE section.
    const winning = active.some((b) => b.isWinning || b.status === 'winning');
    c.status = winning ? 'winning' : 'outbid';
    const pool = active.length ? active : c.bids;
    c.displayAmount = pool.reduce((m, b) => Math.max(m, b.amount), 0);
    allClusters.push(c);
  });

  const clusters = selectedAuctionId === 'all' ? allClusters : allClusters.filter((c) => c.auctionId === selectedAuctionId);
  const winningClusters = clusters.filter((c) => c.status === 'winning');
  const outbidClusters = clusters.filter((c) => c.status === 'outbid');

  const bidLotKeys = new Set(bids.map((b) => `${b.auctionId}_${b.lotId}`));
  const orphanProxies = proxies.filter((p) => !bidLotKeys.has(`${p.auctionId}_${p.lotId}`));
  const visibleOrphans = selectedAuctionId === 'all' ? orphanProxies : orphanProxies.filter((p) => p.auctionId === selectedAuctionId);

  // Newest bid time per lot (for auto-undismiss) — from ALL bids on the lot.
  const newestBidMsByLot: Record<string, number> = {};
  bids.forEach((b) => {
    const key = `${b.auctionId}_${b.lotId}`;
    const ms = b.placedAt ? new Date(b.placedAt).getTime() : 0;
    if (newestBidMsByLot[key] == null || ms > newestBidMsByLot[key]) newestBidMsByLot[key] = ms;
  });
  const isLotDismissed = (key: string) => {
    const dts = dismissedBids[key];
    if (dts == null) return false;
    return (newestBidMsByLot[key] ?? 0) <= dts;
  };

  const outbidActive = outbidClusters.filter((c) => !isLotDismissed(c.key));
  const outbidHidden = outbidClusters.filter((c) => isLotDismissed(c.key));

  // ---------- shared bits ----------
  const restoreBtn = (c: LotCluster) => (
    <button type="button" onClick={(e) => { e.stopPropagation(); restore(c.auctionId, c.lotId); }} className="text-xs text-primary-600 hover:underline">Restore</button>
  );
  // Expanded per-lot bid history — surfaces auto-bid advances and voided bids (greyed).
  const expandedDetail = (c: LotCluster) => (
    <div className="mt-3 pt-3 border-t border-gray-200 space-y-1">
      {c.bids.map((b) => {
        const voided = isVoided(b);
        return (
          <div key={b.id} className={`flex items-center justify-between text-xs ${voided ? 'text-gray-400' : 'text-gray-700'}`}>
            <span className="flex items-center gap-1">
              <span className={`px-1.5 py-0.5 rounded ${isAuto(b) ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-600'}`}>
                {isAuto(b) ? 'Auto-bid' : 'Your bid'}
              </span>
              {voided && <span className="italic">voided{b.voidReason ? ` · ${b.voidReason}` : ''}</span>}
            </span>
            <span className="flex items-center gap-3">
              <span className={voided ? 'line-through' : 'font-semibold'}>${(b.amount / 100).toFixed(2)}</span>
              <span className="text-gray-400">{b.placedAt?.toLocaleString?.() ?? ''}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
  const countChip = (c: LotCluster) =>
    c.activeCount > 1 ? <span className="text-xs bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded-full whitespace-nowrap">{c.activeCount} bids</span> : null;

  // ---------- TILE ----------
  const clusterTile = (c: LotCluster, opts?: { dismissed?: boolean }) => {
    const proxy = proxyByLot.get(c.key);
    const hasProxy = !!proxy;
    const win = c.status === 'winning';
    const canDismiss = c.status === 'outbid' && !hasProxy && !opts?.dismissed;
    const base = win ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200';
    const treatment = hasProxy ? 'bg-indigo-50 border-indigo-300 ring-2 ring-indigo-300 border-l-4 border-l-indigo-500' : `${base} border-2`;
    const expandable = c.bids.length > 1;
    const isOpen = !!expanded[c.key];
    return (
      <div key={c.key} className={`relative p-4 rounded-lg ${treatment} ${opts?.dismissed ? 'opacity-60' : ''}`}>
        {canDismiss && (
          <button type="button" aria-label="Hide from My Bids" title="Hide from My Bids"
            onClick={(e) => { e.stopPropagation(); dismiss(c.auctionId, c.lotId); }}
            className="absolute -top-2 -right-2 z-10 bg-white border rounded-full w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700 shadow leading-none">×</button>
        )}
        {opts?.dismissed && <div className="absolute -top-2 -right-2 z-10 bg-white border rounded-full px-2 h-6 flex items-center shadow">{restoreBtn(c)}</div>}
        {hasProxy && (
          <div className="mb-3 -mx-1 flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-white">
            <span className="text-lg">🤖</span>
            <span className="text-sm font-semibold tracking-wide">AUTO-BID UP TO ${(proxy!.maxAmount / 100).toFixed(2)}</span>
          </div>
        )}
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0 pr-6">
            <Link to={`/auctions/${c.auctionId}/lots/${c.lotId}`} className="font-bold text-gray-800 hover:text-primary-600 block">{c.lotTitle}</Link>
            <p className="text-sm text-gray-600 flex items-center gap-2">LOT {c.lotNumber} {countChip(c)}</p>
            {selectedAuctionId === 'all' && <p className="text-xs text-gray-500">{auctionNames[c.auctionId] || c.auctionId}</p>}
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-sm text-gray-600">Your Bid</p>
            <p className={`text-2xl font-bold ${win ? 'text-green-600' : 'text-red-600'}`}>${(c.displayAmount / 100).toFixed(2)}</p>
            <span className={`inline-block mt-1 px-2 py-1 text-white text-xs font-bold rounded ${win ? 'bg-green-500' : 'bg-red-500'}`}>{win ? 'HIGH BID' : 'OUTBID'}</span>
            <button onClick={() => navigate(`/auctions/${c.auctionId}/lots/${c.lotId}`)}
              className={`mt-2 w-full px-3 py-1 text-white rounded text-sm font-semibold ${win ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
              {win ? 'Manage Bid' : 'Raise Bid Now!'}
            </button>
          </div>
        </div>
        {expandable && (
          <button onClick={() => toggleExpand(c.key)} className="mt-2 text-xs text-gray-500 hover:text-gray-800">
            {isOpen ? '▼ Hide' : '▶ Show'} all {c.bids.length} bids
          </button>
        )}
        {expandable && isOpen && expandedDetail(c)}
      </div>
    );
  };

  const proxyTile = (proxy: ProxyBid) => {
    const meta = lotMeta[`${proxy.auctionId}_${proxy.lotId}`] || {};
    return (
      <div key={proxy.id} className="p-4 rounded-lg bg-indigo-50 border-indigo-300 ring-2 ring-indigo-300 border-l-4 border-l-indigo-500">
        <div className="mb-3 -mx-1 flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-white">
          <span className="text-lg">🤖</span>
          <span className="text-sm font-semibold tracking-wide">AUTO-BID UP TO ${(proxy.maxAmount / 100).toFixed(2)}</span>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <Link to={`/auctions/${proxy.auctionId}/lots/${proxy.lotId}`} className="font-bold text-gray-800 hover:text-primary-600 block">{meta.title || 'Lot'}</Link>
            {meta.lotNumber && <p className="text-sm text-gray-600">LOT {meta.lotNumber}</p>}
            {selectedAuctionId === 'all' && <p className="text-xs text-gray-500">{auctionNames[proxy.auctionId] || proxy.auctionId}</p>}
            <p className="text-xs text-gray-500 mt-1">Auto-bid active — no bid placed yet</p>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="text-sm text-gray-600">Your Max</p>
            <p className="text-2xl font-bold text-indigo-700">${(proxy.maxAmount / 100).toFixed(2)}</p>
            <button onClick={() => navigate(`/auctions/${proxy.auctionId}/lots/${proxy.lotId}`)} className="mt-2 w-full px-3 py-1 bg-indigo-600 text-white rounded text-sm font-semibold hover:bg-indigo-700">View Lot</button>
          </div>
        </div>
      </div>
    );
  };

  // ---------- LIST ----------
  const clusterRow = (c: LotCluster, opts?: { dismissed?: boolean }) => {
    const proxy = proxyByLot.get(c.key);
    const hasProxy = !!proxy;
    const win = c.status === 'winning';
    const canDismiss = c.status === 'outbid' && !hasProxy && !opts?.dismissed;
    const tint = hasProxy ? 'border-l-4 border-l-indigo-500 bg-indigo-50/40' : win ? 'border-l-4 border-l-green-500' : 'border-l-4 border-l-red-500';
    const expandable = c.bids.length > 1;
    const isOpen = !!expanded[c.key];
    return (
      <div key={c.key}>
        <div className={`flex items-center gap-3 py-2 px-3 border rounded ${tint} ${opts?.dismissed ? 'opacity-60' : ''}`}>
          <button onClick={() => expandable && toggleExpand(c.key)} className={`w-4 text-gray-400 shrink-0 ${expandable ? 'hover:text-gray-700' : 'invisible'}`}>{isOpen ? '▼' : '▶'}</button>
          <span className="text-xs font-mono text-gray-500 w-16 shrink-0">LOT {c.lotNumber}</span>
          <Link to={`/auctions/${c.auctionId}/lots/${c.lotId}`} className="flex-1 min-w-0 truncate font-semibold text-gray-800 hover:text-primary-600">{c.lotTitle}</Link>
          {countChip(c)}
          {hasProxy && <span className="hidden sm:inline text-xs bg-indigo-600 text-white px-2 py-0.5 rounded whitespace-nowrap">🤖 ${(proxy!.maxAmount / 100).toFixed(2)}</span>}
          <span className={`w-20 text-right font-bold shrink-0 ${win ? 'text-green-600' : 'text-red-600'}`}>${(c.displayAmount / 100).toFixed(2)}</span>
          <span className={`w-16 text-center text-xs font-bold text-white rounded px-1 py-0.5 shrink-0 ${win ? 'bg-green-500' : 'bg-red-500'}`}>{win ? 'HIGH' : 'OUT'}</span>
          <button onClick={() => navigate(`/auctions/${c.auctionId}/lots/${c.lotId}`)} className={`text-xs font-semibold px-3 py-1 rounded text-white shrink-0 ${win ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>{win ? 'Manage' : 'Raise'}</button>
          <div className="w-12 text-center shrink-0 text-sm">
            {canDismiss && <button type="button" aria-label="Hide from My Bids" title="Hide" onClick={(e) => { e.stopPropagation(); dismiss(c.auctionId, c.lotId); }} className="text-gray-400 hover:text-gray-700 text-lg leading-none">×</button>}
            {opts?.dismissed && restoreBtn(c)}
          </div>
        </div>
        {expandable && isOpen && <div className="ml-7 mr-3 mb-1 px-3 pb-2 border border-t-0 rounded-b bg-white/60">{expandedDetail(c)}</div>}
      </div>
    );
  };

  const proxyRow = (proxy: ProxyBid) => {
    const meta = lotMeta[`${proxy.auctionId}_${proxy.lotId}`] || {};
    return (
      <div key={proxy.id} className="flex items-center gap-3 py-2 px-3 border rounded border-l-4 border-l-indigo-500 bg-indigo-50/40">
        <span className="w-4 shrink-0" />
        <span className="text-xs font-mono text-gray-500 w-16 shrink-0">{meta.lotNumber ? `LOT ${meta.lotNumber}` : '—'}</span>
        <Link to={`/auctions/${proxy.auctionId}/lots/${proxy.lotId}`} className="flex-1 min-w-0 truncate font-semibold text-gray-800 hover:text-primary-600">{meta.title || 'Lot'}</Link>
        <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded whitespace-nowrap shrink-0">🤖 up to ${(proxy.maxAmount / 100).toFixed(2)}</span>
        <button onClick={() => navigate(`/auctions/${proxy.auctionId}/lots/${proxy.lotId}`)} className="text-xs font-semibold px-3 py-1 rounded text-white bg-indigo-600 hover:bg-indigo-700 shrink-0">View</button>
        <div className="w-12 shrink-0" />
      </div>
    );
  };

  const clusterItem = (c: LotCluster, opts?: { dismissed?: boolean }) => (viewMode === 'list' ? clusterRow(c, opts) : clusterTile(c, opts));
  const proxyItem = (proxy: ProxyBid) => (viewMode === 'list' ? proxyRow(proxy) : proxyTile(proxy));
  const listWrap = viewMode === 'list' ? 'space-y-2' : 'space-y-3';

  const activeProxyCount = selectedAuctionId === 'all' ? proxies.length : proxies.filter((p) => p.auctionId === selectedAuctionId).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-bold">My Bids</h1>
        <div className="flex items-center gap-3">
          <div className="inline-flex rounded border overflow-hidden">
            <button onClick={() => setView('tile')} className={`px-3 py-2 text-sm font-semibold ${viewMode === 'tile' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Tiles</button>
            <button onClick={() => setView('list')} className={`px-3 py-2 text-sm font-semibold border-l ${viewMode === 'list' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>List</button>
          </div>
          {auctionOptions.length > 1 && (
            <select value={selectedAuctionId} onChange={(e) => setSelectedAuctionId(e.target.value)} className="input py-2" aria-label="Filter by sale">
              <option value="all">All sales</option>
              {auctionOptions.map((id) => (<option key={id} value={id}>{auctionNames[id] || id}</option>))}
            </select>
          )}
          <Link to="/browse" className="btn-secondary">Browse Auctions</Link>
        </div>
      </div>

      {activeProxyCount > 0 && (
        <p className="text-sm text-indigo-700">🤖 You have {activeProxyCount} active auto-bid{activeProxyCount === 1 ? '' : 's'}. Lots with an auto-bid are outlined in indigo and show your max.</p>
      )}

      {visibleOrphans.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4 text-indigo-700 flex items-center gap-2">🤖 Auto-bids set ({visibleOrphans.length})</h2>
          <div className={listWrap}>{visibleOrphans.map((p) => proxyItem(p))}</div>
        </div>
      )}

      {winningClusters.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4 text-green-600 flex items-center gap-2">🏆 Winning ({winningClusters.length})</h2>
          <div className={listWrap}>{winningClusters.map((c) => clusterItem(c))}</div>
        </div>
      )}

      {outbidActive.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-bold mb-4 text-red-600 flex items-center gap-2">⚠️ Outbid ({outbidActive.length})</h2>
          <div className={listWrap}>{outbidActive.map((c) => clusterItem(c))}</div>
        </div>
      )}

      {outbidHidden.length > 0 && (
        <div className="card">
          <button onClick={() => setShowDismissed((s) => !s)} className="text-sm font-semibold text-gray-600 hover:text-gray-800">
            {showDismissed ? '▼' : '▶'} Show dismissed ({outbidHidden.length})
          </button>
          {showDismissed && <div className={`${listWrap} mt-4`}>{outbidHidden.map((c) => clusterItem(c, { dismissed: true }))}</div>}
        </div>
      )}

      {winningClusters.length === 0 && outbidActive.length === 0 && outbidHidden.length === 0 && visibleOrphans.length === 0 && (
        <div className="card text-center py-8"><p className="text-gray-600">No bids for this sale.</p></div>
      )}
    </div>
  );
};

export default MyBids;
